'use client';

import { useMemo, useCallback } from 'react';
import { Country, CorporationType } from '@prisma/client';
import { useSupportedCorporations } from '../../../../../hooks/useSupportedCorporations';
import { CorporationModel } from '@commenda/utils/models/corporation';
import { CountryCodeMap, PRIORITY_COUNTRIES } from '@commenda/kakashi';

/**
 * Options for dropdown select components
 */
export interface DropdownOption<T> {
  value: T;
  label: string;
}

/**
 * Return type for useBusinessEntityFields hook
 */
export interface BusinessEntityFieldsResult {
  // Loading and error states
  isLoading: boolean;
  error: unknown;

  // Country options (always available)
  countryOptions: DropdownOption<Country>[];

  // Get jurisdiction options for a specific country
  getJurisdictionOptions: (country: Country | null) => DropdownOption<string>[];

  // Get entity type options for a specific country and jurisdiction, valued by legal form id
  getEntityTypeOptions: (
    country: Country | null,
    jurisdiction: string | null
  ) => DropdownOption<string>[];

  // The legacy name for a picked legal form, where the vocabulary has one
  getCorporationTypeForLegalForm: (
    country: Country | null,
    jurisdiction: string | null,
    entityLegalFormId: string | null
  ) => CorporationType | null;

  // Helper to check if jurisdiction should be enabled
  isJurisdictionEnabled: (country: Country | null) => boolean;

  // Helper to check if entity type should be enabled
  isEntityTypeEnabled: (
    country: Country | null,
    jurisdiction: string | null
  ) => boolean;

  // Auto-select helpers (returns the value if there's only one option)
  getAutoSelectJurisdiction: (country: Country | null) => string | null;
  getAutoSelectEntityLegalFormId: (
    country: Country | null,
    jurisdiction: string | null
  ) => string | null;
}

/**
 * Hook for managing business entity field options and cascading logic
 * 
 * This hook provides:
 * - Country options with priority countries at the top
 * - Jurisdiction options that depend on selected country
 * - Entity type options that depend on selected country and jurisdiction
 * - Auto-select logic when there's only one option
 * 
 * @example
 * ```tsx
 * const {
 *   countryOptions,
 *   getJurisdictionOptions,
 *   getEntityTypeOptions,
 *   isJurisdictionEnabled,
 *   isEntityTypeEnabled,
 * } = useBusinessEntityFields();
 * 
 * // Use in component
 * <Select options={countryOptions} />
 * <Select 
 *   options={getJurisdictionOptions(selectedCountry)} 
 *   disabled={!isJurisdictionEnabled(selectedCountry)}
 * />
 * ```
 */
export function useBusinessEntityFields(): BusinessEntityFieldsResult {
  const { data: supportedCorporations, error } = useSupportedCorporations();

  const isLoading = !supportedCorporations && !error;

  // Build country options with priority countries at the top
  const countryOptions = useMemo<DropdownOption<Country>[]>(() => {
    if (!supportedCorporations) return [];

    const allCountries = Object.keys(supportedCorporations.corporations) as Country[];

    // Separate priority and other countries
    const prioritySet = new Set(PRIORITY_COUNTRIES);
    const priorityCountries = PRIORITY_COUNTRIES.filter(c => allCountries.includes(c));
    const otherCountries = allCountries
      .filter(c => !prioritySet.has(c))
      .sort((a, b) => a.localeCompare(b));

    // Map to dropdown options using CountryCodeMap as the canonical name source
    const mapToOption = (country: Country): DropdownOption<Country> => ({
      value: country,
      label: CountryCodeMap[country] || country,
    });

    return [...priorityCountries.map(mapToOption), ...otherCountries.map(mapToOption)];
  }, [supportedCorporations]);

  // Get jurisdiction options for a country
  const getJurisdictionOptions = useCallback(
    (country: Country | null): DropdownOption<string>[] => {
      if (!country || !supportedCorporations) return [];

      const jurisdictions = CorporationModel.getJurisdictionList(
        country,
        supportedCorporations
      );

      return jurisdictions.map(j => ({
        value: j.id,
        label: j.displayName,
      }));
    },
    [supportedCorporations]
  );

  // Get entity type options for a country and jurisdiction. Valued by legal form id: most forms
  // the content carries have no corporation type, and keying by one drops them.
  const getEntityTypeOptions = useCallback(
    (
      country: Country | null,
      jurisdiction: string | null
    ): DropdownOption<string>[] => {
      if (!country || !jurisdiction || !supportedCorporations) return [];

      return CorporationModel.getEntityTypeOptions(
        country,
        jurisdiction,
        supportedCorporations
      ).map(form => ({
        value: form.entityLegalFormId,
        label: form.displayName,
      }));
    },
    [supportedCorporations]
  );

  // The legacy name for a picked form, where the vocabulary has one. Derived, never picked.
  const getCorporationTypeForLegalForm = useCallback(
    (
      country: Country | null,
      jurisdiction: string | null,
      entityLegalFormId: string | null
    ): CorporationType | null => {
      if (!country || !jurisdiction || !entityLegalFormId) return null;

      const form =
        supportedCorporations?.entityTypes?.[country]?.[jurisdiction]?.[
          entityLegalFormId
        ];
      if (!form) return null;

      // The create contract requires a type and the column is not nullable. Unknown is what that
      // column already carries for a form the legacy vocabulary never named.
      return form.corporationType ?? CorporationType.Unknown;
    },
    [supportedCorporations]
  );

  // Check if jurisdiction should be enabled
  const isJurisdictionEnabled = useCallback(
    (country: Country | null): boolean => {
      return !!country;
    },
    []
  );

  // Check if entity type should be enabled
  const isEntityTypeEnabled = useCallback(
    (country: Country | null, jurisdiction: string | null): boolean => {
      return !!country && !!jurisdiction;
    },
    []
  );

  // Auto-select jurisdiction if there's only one option
  const getAutoSelectJurisdiction = useCallback(
    (country: Country | null): string | null => {
      if (!country || !supportedCorporations) return null;

      const jurisdictions = CorporationModel.getJurisdictionList(
        country,
        supportedCorporations
      );

      if (jurisdictions.length === 1) {
        return jurisdictions[0].id;
      }

      return null;
    },
    [supportedCorporations]
  );

  // Auto-select entity type if there's only one option
  const getAutoSelectEntityLegalFormId = useCallback(
    (country: Country | null, jurisdiction: string | null): string | null => {
      if (!country || !jurisdiction || !supportedCorporations) return null;

      const forms = CorporationModel.getEntityTypeOptions(
        country,
        jurisdiction,
        supportedCorporations
      );

      return forms.length === 1 ? forms[0].entityLegalFormId : null;
    },
    [supportedCorporations]
  );

  return {
    isLoading,
    error,
    countryOptions,
    getJurisdictionOptions,
    getEntityTypeOptions,
    getCorporationTypeForLegalForm,
    isJurisdictionEnabled,
    isEntityTypeEnabled,
    getAutoSelectJurisdiction,
    getAutoSelectEntityLegalFormId,
  };
}
